import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fetchCaption, fetchSrt, subscribeJob, type JobLogLine } from "../../api";
import { useIsMobile } from "../../lib/useIsMobile";

/**
 * The read-only views of a clip's text artefacts: the caption file, the post
 * caption, the job log. They were the video page's centre tabs; they live in
 * the Details dialog now (spec 2026-09-16 §2.2).
 */

export function TranscriptTab({ jobId, hasVideo }: { jobId: string; hasVideo: boolean }): React.JSX.Element {
  const [srt, setSrt] = useState<string | null | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    fetchSrt(jobId)
      .then((data) => { if (!cancelled) setSrt(data); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => {
      cancelled = true;
    };
  }, [jobId, hasVideo]);

  if (loadError) return <Empty>Couldn't load the captions.</Empty>;
  if (srt === undefined) {
    return <Empty>Loading captions…</Empty>;
  }
  if (srt === null) {
    return <Empty>The captions aren't written yet — they come right after the transcript.</Empty>;
  }
  return (
    <pre
      dir="rtl"
      style={{
        fontFamily: "var(--ql-font-arabic)",
        fontSize: 14,
        lineHeight: 1.7,
        margin: 0,
        whiteSpace: "pre-wrap",
        color: "var(--ql-gray-200)",
        maxHeight: 540,
        overflow: "auto",
      }}
    >
      {srt}
    </pre>
  );
}

export function CaptionTab({ jobId, hasVideo }: { jobId: string; hasVideo: boolean }): React.JSX.Element {
  const [text, setText] = useState<string | null | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    fetchCaption(jobId)
      .then((data) => { if (!cancelled) setText(data); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => {
      cancelled = true;
    };
  }, [jobId, hasVideo]);

  const handleCopy = async (): Promise<void> => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignored */
    }
  };

  if (loadError) return <Empty>Couldn't load the post caption.</Empty>;
  if (text === undefined) return <Empty>Loading the post caption…</Empty>;
  if (text === null) return <Empty>The post caption is written after the video is built.</Empty>;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ql-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          Ready to paste · Instagram · TikTok · Reels
        </span>
        <button
          onClick={handleCopy}
          style={{
            background: "rgba(255,213,0,.14)",
            border: "1px solid rgba(255,213,0,.30)",
            color: "var(--ql-yellow)",
            padding: "4px 12px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: "inherit",
          }}
        >
          {copied ? <Check size={12} strokeWidth={2.5} /> : <Copy size={12} strokeWidth={2} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div
        style={{
          padding: 18,
          borderRadius: 14,
          background: "rgba(255,255,255,.02)",
          border: "1px solid rgba(255,255,255,.06)",
          whiteSpace: "pre-wrap",
          fontSize: 14,
          lineHeight: 1.65,
          color: "var(--ql-gray-200)",
          fontFamily: "var(--ql-font-sans)",
        }}
      >
        {text}
      </div>
    </div>
  );
}

export function LogsTab({ jobId }: { jobId: string }): React.JSX.Element {
  const isMobile = useIsMobile();
  const [lines, setLines] = useState<JobLogLine[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  // Subscribe for live log streaming. Replays logs already in the store first.
  useEffect(() => {
    setLines([]);
    const es = subscribeJob(jobId, {
      update: (job) => setLines(job.logs),
      log: (line) => setLines((cur) => [...cur.slice(-499), line]),
    });
    return () => es.close();
  }, [jobId]);

  // Tail to bottom whenever new lines come in.
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [lines]);

  if (lines.length === 0) return <Empty>No log yet — lines appear here as the video is built.</Empty>;

  return (
    <div
      ref={ref}
      style={{
        fontFamily: "var(--ql-font-mono)",
        fontSize: 12,
        lineHeight: 1.7,
        maxHeight: 540,
        overflow: "auto",
      }}
    >
      {lines.map((l, i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "56px 1fr" : "76px 56px 100px 1fr",
            gap: 10,
            padding: "4px 0",
            borderBottom: "1px dashed rgba(255,255,255,.04)",
          }}
        >
          {!isMobile && (
            <span style={{ color: "var(--ql-fg-subtle)" }}>
              {new Date(l.ts).toLocaleTimeString()}
            </span>
          )}
          <span
            style={{
              color:
                l.level === "warn"
                  ? "#FACC15"
                  : l.level === "error"
                  ? "#F87171"
                  : l.level === "ok"
                  ? "#4ADE80"
                  : "#60A5FA",
              fontWeight: 700,
            }}
          >
            {l.level.toUpperCase()}
          </span>
          {!isMobile && (
            <span style={{ color: "var(--ql-yellow)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {l.stage ?? ""}
            </span>
          )}
          <span style={{ color: "var(--ql-gray-300)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{l.msg}</span>
        </div>
      ))}
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        textAlign: "center",
        color: "var(--ql-fg-muted)",
        padding: 40,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
